import { useCart } from "@/context/CartContext";
import { useSettings, computeTotals } from "@/context/SettingsContext";
import { useNavigate } from "react-router-dom";
import { money } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingCart, Package } from "lucide-react";
import FreeShippingBar from "@/components/FreeShippingBar";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export default function CartDrawer() {
  const { items, updateQty, remove, subtotal, open, setOpen } = useCart();
  const settings = useSettings();
  const navigate = useNavigate();
  const t = computeTotals(subtotal, settings);
  const [shipText, setShipText] = useState("Calculated at checkout");
  useEffect(() => {
    if (open) api.get("/shipping/settings").then((r) => r.data.messages?.cart && setShipText(r.data.messages.cart)).catch(() => {});
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" className="w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="px-5 py-4 border-b">
          <SheetTitle className="font-heading flex items-center gap-2">
            <ShoppingCart size={18} /> Your Cart
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 grid place-items-center text-muted-foreground text-sm">
            Your cart is empty.
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              <FreeShippingBar subtotal={subtotal} />
              {items.map((i) => (
                <div key={i.id} data-testid={`cart-item-${i.sku}`} className="flex gap-3 border-b pb-4">
                  {i.image_url ? (
                    <img src={i.image_url} alt={i.name} className="w-16 h-16 rounded-md object-cover bg-slate-100"
                      onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <div className="w-16 h-16 rounded-md bg-slate-100 grid place-items-center text-slate-300 shrink-0">
                      <Package size={24} strokeWidth={1.5} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{i.name}</div>
                    <div className="text-xs font-mono text-muted-foreground">{i.sku}</div>
                    <div className="mt-1 text-sm font-mono text-accent font-bold">{money(i.price)}</div>
                    <div className="mt-2 flex items-center gap-2">
                      <button data-testid={`cart-dec-${i.sku}`} onClick={() => updateQty(i.id, i.qty - 1)} aria-label={`Decrease quantity of ${i.name}`}
                        className="w-9 h-9 grid place-items-center rounded border hover:bg-slate-100"><Minus size={14} /></button>
                      <span className="w-8 text-center text-sm font-medium" aria-live="polite">{i.qty}</span>
                      <button data-testid={`cart-inc-${i.sku}`} onClick={() => updateQty(i.id, i.qty + 1)} aria-label={`Increase quantity of ${i.name}`}
                        className="w-9 h-9 grid place-items-center rounded border hover:bg-slate-100"><Plus size={14} /></button>
                      <button data-testid={`cart-remove-${i.sku}`} onClick={() => remove(i.id)} aria-label={`Remove ${i.name}`}
                        className="ml-auto w-9 h-9 grid place-items-center text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t px-5 py-4 space-y-2 bg-slate-50">
              <Row label="Subtotal (incl GST)" value={money(t.subtotal)} testid="cart-subtotal" />
              <Row label="Shipping" value={<span className="font-sans text-muted-foreground">{shipText}</span>} testid="cart-shipping" />
              <Row label="GST (15%) included" value={money(t.subtotal * 3 / 23)} muted testid="cart-gst" />
              <div className="flex justify-between pt-2 border-t font-bold text-lg">
                <span>Total <span className="text-xs font-normal text-muted-foreground">(excl. shipping)</span></span>
                <span data-testid="cart-total" className="font-mono">{money(t.subtotal)}</span>
              </div>
              <Button data-testid="proceed-checkout-btn" onClick={() => { setOpen(false); navigate("/checkout"); }}
                className="w-full h-11 mt-2 bg-accent text-steel hover:bg-amber-600 font-semibold">
                Proceed to Checkout
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Row({ label, value, muted, testid }) {
  return (
    <div className={`flex justify-between text-sm ${muted ? "text-muted-foreground" : ""}`}>
      <span>{label}</span>
      <span data-testid={testid} className="font-mono">{value}</span>
    </div>
  );
}
